import { createFileRoute, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { BarChart3Icon, BriefcaseIcon, EllipsisVerticalIcon, FileTextIcon, LayoutGridIcon } from 'lucide-react'
import {
  NavStack,
  NavStackStage,
  NavStackScreen,
  usePreviewMode,
  NS_PASSTHROUGH_CLASS,
} from '@/components/custom/route-transition'
import { Button } from '@/components/ui/button'
import { SlideInPanel } from './-components/shared/layout'
import { TransitionTabsSurface } from './-components/shared/transition-tabs'
import { PlaygroundJobsList, PlaygroundPeopleList, playgroundJobName } from './-components/dev/transition-playground'

/**
 * The STACKED member of the transition playground, built like Jobs: the list, a job's people, and a person's report
 * whose tabs are the inner lateral pager — three levels, the report's Distribution tab hosting a pager of its own.
 * What each level carries and why: docs/notes/app/transition-playground.md
 */
export const Route = createFileRoute('/_app/admin/transitions/jobs')({
  component: RouteComponent,
})

const SCOPE = '/admin/transitions/jobs'
// Asked of a PATHNAME, as a section's ladder is, so the eager push can ask it of a URL not navigated to yet.
const JOB_OPEN = /^\/admin\/transitions\/jobs\/[^/]+/
const REPORT_OPEN = /\/(report|distribution|board)(\/|$)/

function RouteComponent() {
  const navigate = useNavigate()
  const { jobId } = useParams({ strict: false })
  const reportOpen = useRouterState({ select: (state) => REPORT_OPEN.test(state.location.pathname) })
  // Falls through for a preview below, renders the whole level for one above — docs/notes/app/transition-playground.md.
  if (usePreviewMode(SCOPE) === 'passthrough') return <Outlet />
  const pop = (fromDepth: number) => {
    if (fromDepth >= 2 && jobId) void navigate({ to: '/admin/transitions/jobs/$jobId', params: { jobId } })
    else void navigate({ to: SCOPE })
  }
  return (
    <NavStack levels={[true, JOB_OPEN, REPORT_OPEN]} onPop={pop}>
      <NavStackStage className="flex h-full min-h-0 w-full max-md:h-[70dvh]" style={{ containerType: 'inline-size' }}>
        <NavStackScreen
          index={0}
          title="Jobs"
          leading={
            <span className="bg-primary/10 text-primary flex size-8 items-center justify-center rounded-md">
              <BriefcaseIcon className="size-4" />
            </span>
          }
        >
          <PlaygroundJobsList selected={jobId} />
        </NavStackScreen>
        {/* The section's own nesting, verbatim: the desktop grouping wrapper, which `NS_PASSTHROUGH_CLASS` turns into
            no box at all on mobile so the levels inside position against the stage. */}
        <SlideInPanel isOpen={jobId !== undefined} innerClassName="flex" className={NS_PASSTHROUGH_CLASS}>
          <NavStackScreen
            index={1}
            // From the ROUTE, so it empties on the pop's commit rather than when the stack hands over.
            title={playgroundJobName(jobId)}
            actions={
              jobId ? (
                <Button variant="ghost" size="icon" aria-label="Job actions" className="size-8">
                  <EllipsisVerticalIcon className="size-4" />
                </Button>
              ) : null
            }
          >
            {jobId ? <PlaygroundPeopleList jobId={jobId} /> : null}
          </NavStackScreen>
          <NavStackScreen index={2} title={reportOpen ? 'Report' : null}>
            {reportOpen && jobId ? <ReportTabs jobId={jobId} /> : null}
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </NavStack>
  )
}

function ReportTabs(props: { jobId: string }) {
  // A pager's members are CONCRETE paths, so the scope carries the id; its segments are still checked against the
  // `$jobId` route's children, which is what the pattern type is for.
  // oxlint-disable-next-line no-unsafe-type-assertion -- the concrete path of that very route
  const scope = `/admin/transitions/jobs/${props.jobId}` as '/admin/transitions/jobs/$jobId'
  return (
    <TransitionTabsSurface
      scope={scope}
      scrollablePanes
      tabs={[
        { segment: 'report', label: 'Report', icon: FileTextIcon },
        { segment: 'distribution', label: 'Distribution', icon: BarChart3Icon },
        { segment: 'board', label: 'Board', icon: LayoutGridIcon },
      ]}
    />
  )
}
